import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack/stack";
import { Typography } from "@/core/components/ui/typography";
import { isNavigationItemActive } from "@/core/utils/url";
import { cn } from "@/core/utils/utils";

export interface NavListItemDef {
  title: string;
  href: string;
  icon?: React.ElementType;
  show?: boolean;
  exact?: boolean;
}

export interface NavSection {
  title?: string;
  items: NavListItemDef[];
}

export interface NavigationSidebarProps {
  basePath: string;
  sections: NavSection[];
  isLoading?: boolean;
  onNavigate?: (href: string) => void;
  /** Mark this item active when at basePath ("" or "/"). Example: "/pending-spiffs" */
  defaultHref?: string;
}

interface NavListItemProps {
  title: string;
  onClick: () => void;
  active?: boolean;
  isLoading?: boolean;
  icon?: React.ElementType;
  isSubItem?: boolean;
}

const NavListItem = React.memo(
  ({
    title,
    onClick,
    active,
    isLoading,
    icon: Icon,
    isSubItem = false,
  }: NavListItemProps) => {
    if (isLoading) {
      return <Skeleton className="h-12 w-full rounded-full" />;
    }

    return (
      <Button
        variant="ghost"
        onClick={onClick}
        fullWidth
        className={cn("justify-start gap-2 px-4 py-2 text-lg", {
          "ml-4": isSubItem,
          "bg-green-20": active,
        })}
        data-testid={`navigation-sidebar-${title}`}
      >
        {Icon && <Icon className="h-5 w-5" />}
        {title}
      </Button>
    );
  },
);

export const NavigationSidebar = ({
  basePath,
  sections,
  isLoading,
  onNavigate: customOnNavigate,
  defaultHref,
}: NavigationSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const filteredSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        items: section.items.filter(({ show }) => show !== false),
      })),
    [sections],
  );

  const onNavigate = (href: string) => {
    if (customOnNavigate) {
      customOnNavigate(href);
    }
    navigate(`${basePath}${href}`);
  };

  const isActive = useCallback(
    (href: string) =>
      isNavigationItemActive(location.pathname, basePath, href, defaultHref),
    [location.pathname, basePath, defaultHref],
  );

  return (
    <Stack gap="lg" className="ml-6 w-[212px] min-w-[212px] pt-8">
      {filteredSections.map((section, sectionIndex) => (
        <Stack gap="sm" fullWidth key={sectionIndex}>
          {section.title && (
            <Typography
              variant="subtitleW3"
              textColor="tertiary"
              className="p-4 pb-2"
            >
              {section.title}
            </Typography>
          )}
          {section.items.map((item) => (
            <NavListItem
              key={item.title}
              title={item.title}
              onClick={() => onNavigate(item.href)}
              active={
                item.exact
                  ? location.pathname.replace(/\/$/, "") ===
                    `${basePath}${item.href}`.replace(/\/$/, "")
                  : isActive(item.href)
              }
              isLoading={isLoading}
              icon={item.icon}
              isSubItem={!!section.title}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
};
